/**
 * Feature Symbols
 * Created at 2023/09/22
 */
import * as _ from 'lodash-es';
import { Feature } from 'ol';
import { Coordinate, distance } from 'ol/coordinate';
import { getCenter } from 'ol/extent';
import {
  Circle,
  SimpleGeometry,
  Point,
  Polygon,
  LineString,
  Geometry,
} from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import { Style } from 'ol/style';

import { log, warn } from '@/utils/logger';

import {
  GO,
  SymbolType,
  Segment,
  SYMBOL_REVISION as REV,
  USER_VALIDATED_FIELD as UVF,
  middleOfLineString,
  BPSymbol,
  LineSymbol,
  RectSymbol,
  BrokenLineSymbol,
  FIELD_MP,
  FEATURESTATE as FST,
  resolveSpecVersion,
} from '../types';

import {
  FITTINGDTL,
  FITTINGSIZE,
  FITTINGVER,
  FITTINGTYPE,
  FITTING_EC,
  FITTING_STO,
  DEFAULT_PURPLE_COLOR,
  DEFAULT_DOT_RADIUS,
  DEFAULT_NOTE_TXT,
  DEFAULT_EQUIPMENT_FONT_SIZE as DEF,
  DEFAULT_EC_TXT as EC,
  LENGTH_DG,
  HEIGHT_ENHANCE_IN_DIAGONAL as HTD,
} from './config';
import { isLineGeometry } from './geometry';
import { composeLabelBySymbol } from './labelComposer';
import { composeFont } from './styleUtils';
import { lengthColors } from './Palette';
import {
  basicTextStyle,
  basiceLengthStyle,
  basicPolygonStyle,
  basicCircleStyle,
  basicDiamonStyle,
  basicTriangleStyle,
  baseNoteRenderStyle,
  buildComplexTextStyle,
} from './styleBuilder';

// Reuse a single offscreen canvas for text measurement across all calls to
// buildRichLabelFeature — avoids allocating a new canvas element per feature.
const _measureCtx = document
  .createElement('canvas')
  .getContext('2d') as CanvasRenderingContext2D;

export class FeaturesManager {
  /**
   * features cache
   */
  protected _features = new Map<string, Feature<SimpleGeometry>>();
  /**
   * features visualization container
   */
  protected _vectorLayer: VectorLayer<Feature>;

  /**
   * manual polygon features container
   */
  protected _polygonLayer: VectorLayer<Feature>;

  /**
   * keep the very latest feature added to the cache, for elbow deletion on ctrl key relase
   */
  protected _latestFeature: string | null = null;

  constructor(layer: VectorLayer<Feature>, polygonCtnr: VectorLayer<Feature>) {
    this._vectorLayer = layer;
    this._polygonLayer = polygonCtnr;
  }

  get features() {
    return Array.from(this._features.values());
  }

  get labelFeatures() {
    return this.features.filter((f) => {
      const symbolType = f.get(FITTINGTYPE) as SymbolType;
      return symbolType == SymbolType.LABEL;
    });
  }

  /**
   * create and cache line feature from symbol
   * @param line line symbol
   * @param detail
   * @returns
   */
  createLineFeatureBy(line: LineSymbol) {
    const { entityType } = line.detail;
    const line1Feature = this.buildLineSegmentFeature(
      line.segment,
      line.id,
      entityType,
    );
    this.unifiedPatchFeature(
      line1Feature,
      { ...line.detail, id: line.id },
      REV.M,
      line.type,
    );
    // show it:
    this.addFeature(line1Feature);
    // save it to cache
    this.cacheNewFeature(line.id, line1Feature);
    return line1Feature;
  }

  /**
   * create and cache rectangle feature from symbol
   * @param rect rectangle symbol
   * @returns
   */
  createRectFeatureBy(rect: RectSymbol) {
    const rectFeature = this.buildRectangleFeature(
      rect.x,
      rect.y,
      rect.width,
      rect.height,
      rect.id,
    );
    this.unifiedPatchFeature(
      rectFeature,
      { ...rect.detail, id: rect.id },
      REV.M,
      rect.type,
    );
    // show it:
    this.addFeature(rectFeature);
    // save it to cache
    this.cacheNewFeature(rect.id, rectFeature);
    return rectFeature;
  }

  createBrokenLineFeatureBy(brokenLine: BrokenLineSymbol) {
    const { entityType } = brokenLine.detail;
    const feature = this.buildBrokenLineFeature(
      brokenLine.segments,
      brokenLine.id,
      entityType,
    );
    this.unifiedPatchFeature(
      feature,
      { ...brokenLine.detail, id: brokenLine.id },
      REV.M,
      brokenLine.type,
    );
    this.addFeature(feature);
    this.cacheNewFeature(brokenLine.id, feature);
    return feature;
  }

  getFeatureBy(id: string) {
    return this._features.get(id);
  }

  getFeatureByUkey(ukey: string) {
    return this.features.find((f) => f.get('ukey') == ukey);
  }

  /**
   * only return the first one found
   * @param coordinate
   * @returns
   */
  getFeatureAt([x, y]: Coordinate) {
    return this.features.find((f) => {
      const geometry = f.getGeometry();
      if (!geometry) return false;
      const simpleGMT = geometry as SimpleGeometry;
      return simpleGMT.containsXY(x, y);
    });
  }

  /**
   * get length(longer side) of the rectanglar box
   * @param rectFeature box symbol
   * @returns length in pixesl
   */
  getFeatureLongerSide(rectFeature: Feature) {
    const rect = rectFeature.getGeometry() as Polygon;
    // bottom < top, and both negtive value!
    const [left, bottom, right, top] = rect.getExtent();
    const width = Math.round(right - left);
    const height = Math.round(top - bottom);
    const longerSide = Math.max(width, height);
    return longerSide;
  }

  /**
   * get diagonal of the rectanglar box
   * @param rectFeature box symbol
   * @returns length in pixesl
   */
  getFeatureDiagonal(rectFeature: Feature) {
    const rect = rectFeature.getGeometry() as Polygon;
    // bottom < top, and both negtive value!
    const [left, bottom, right, top] = rect.getExtent();
    const width = Math.round(right - left);
    // ! add a small offset - @2026/03/12
    const height = Math.round(top - bottom) * HTD;
    const diagonal = Math.sqrt(width * width + height * height);
    return diagonal;
  }

  hasActiveFeature() {
    const isActiveFeature = (feature: Feature) => {
      const status = feature.get(FST.A);
      return status == true;
    };
    const result = this.features.find(isActiveFeature);
    return result != undefined;
  }

  /**
   * Get the most closest feature to the coordinate
   * @param point
   * @param threshold
   */
  searchLabelInside(box: SimpleGeometry, threshold = 10) {
    return this.labelFeatures.find((f) => {
      const geometry = f.getGeometry();
      if (!geometry) return false;
      const point = geometry as Point;
      const labelXY = point.getCoordinates();
      if (isLineGeometry(geometry)) {
        const line = geometry as LineString;
        // const middlePt = getCenter(line.getExtent());
        const middlePt = middleOfLineString(line);
        return distance(labelXY, middlePt) < threshold;
      }
      return box.intersectsCoordinate(labelXY);
    });
  }

  searchLabelBy(fatherUkey: string) {
    return this.labelFeatures.find((f) => {
      const detail = f.get(FITTINGDTL);
      return detail['father'] == fatherUkey;
    });
  }

  keepFeatureBy(id: string, feature: Feature<SimpleGeometry>) {
    this._features.set(id, feature);
  }

  isEmpty() {
    return !this._features.size;
  }

  /**
   * make a feature clone to render it front
   *
   * @param feature to clone
   * @returns
   */
  moveFeatureFront(targetFeature: Feature | undefined) {
    const source = this._vectorLayer.getSource();
    if (!source) return null;
    if (!targetFeature) return null;
    // safe to go
    source.removeFeature(targetFeature);
    const clone = targetFeature.clone();
    const featureId = targetFeature.getId() as string;
    clone.setId(featureId);
    source.addFeature(clone);
    this.cacheNewFeature(featureId, clone);
    return clone;
  }

  /**
   * Show the featue to canvas
   * @param feature
   */
  addFeature(feature: Feature) {
    this._vectorLayer.getSource()?.addFeature(feature);
  }

  addPolygon(polygon: Feature) {
    this._polygonLayer.getSource()?.addFeature(polygon);
  }

  /**
   * lock a polygon insulation area
   * @param polygon
   */
  lockPolygon(polygon: Feature) {
    polygon.set(FST.K, true);
  }

  /**
   * unlock a polygon insulation area
   * @param polygon
   */
  unlockPolygon(polygon: Feature) {
    polygon.unset(FST.K);
  }

  /**
   * show insulation in the polygon layer
   * @param insulation
   */
  addInsulation(insulation: Feature) {
    this._polygonLayer.getSource()?.addFeature(insulation);
  }

  /**
   * Display all features on map vector layer
   *
   * - PUT `POLYGON_AREA` feature into `_polygonLayer`...
   * TODO: put `INSULALTION_???` items above `POLYGON_AREA`s into `_polygonLayer` as well!
   *
   * @param features
   */
  showFeatures(features: Feature[]) {
    const polyFilter = (f: Feature) => {
      return f.get(FITTINGTYPE) == SymbolType.POLYGON_AREA;
    };
    // == step-1: put polygons on polygon layer ==
    const polyAreas = features.filter(polyFilter);
    this._polygonLayer.getSource()?.addFeatures(polyAreas);
    // == step-2: put normal features on symbol layer ==
    const comparor = (f: Feature) => f.getId();
    const normalFeatures = _.differenceBy(features, polyAreas, comparor);
    this._vectorLayer.getSource()?.addFeatures(normalFeatures);
  }

  cacheNewFeature(id: string, feature: Feature) {
    const simpleFeature = feature as Feature<SimpleGeometry>;
    this._features.set(id, simpleFeature);
    this._latestFeature = id;
  }

  /**
   * Create feature for T-junction
   * @param x
   * @param y
   * @param radius
   * @param id
   * @returns
   */
  buildTriangleFeature(
    x: number,
    y: number,
    radius: number,
    id: string,
  ): Feature {
    const geometry = new Point([x, -y]);
    const triangleFeature = new Feature({ geometry });
    triangleFeature.setId(id);
    const style = basicTriangleStyle('blue', 2);
    triangleFeature.setStyle(style);
    return triangleFeature;
  }

  /**
   * Create feature for Offset-elbow at first rendering!!
   * @param x
   * @param y
   * @param radius
   * @param id
   * @returns
   */
  buildDiamondFeature(
    x: number,
    y: number,
    radius: number,
    id: string,
  ): Feature {
    const geometry = new Point([x, -y]);
    const diamondFeature = new Feature({ geometry });
    diamondFeature.setId(id);
    const style = basicDiamonStyle('blue', 1, 2);
    diamondFeature.setStyle(style);
    return diamondFeature;
  }

  buildSimpleCircle(x: number, y: number, radius: number) {
    return new Circle([x, y], radius);
  }

  buildDotFeature(
    x: number,
    y: number,
    id: string,
    radius = DEFAULT_DOT_RADIUS,
  ): Feature {
    const nagtiveY = -Math.abs(y);
    const geometry = new Circle([x, nagtiveY], radius);
    const dot = new Feature({ geometry });
    dot.setId(id);
    dot.setStyle(basicCircleStyle());
    return dot;
  }

  buildLineSegmentFeature(
    segment: Segment,
    id: string,
    entityType: string,
    stroke?: number,
    withDash?: boolean,
  ) {
    const [[x1, y1], [x2, y2]] = segment;
    const geometry = new LineString([
      [x1, -Math.abs(y1)], // keep it negative to showup on the map
      [x2, -Math.abs(y2)], // keep it negative to showup on the map
    ]);
    const style = basiceLengthStyle(lengthColors[entityType], stroke, withDash);
    const lineFeature = new Feature({ geometry });
    lineFeature.setId(id);
    lineFeature.setStyle(style);
    return lineFeature;
  }

  buildBrokenLineFeature(segments: Segment[], id: string, entityType = 'FLEX') {
    const coordinates: Coordinate[] = [];
    for (let i = 0; i < segments.length; i++) {
      const head = segments[i][0];
      const tail = segments[i][1];
      const lastPt = coordinates.length ? coordinates.slice(-1)[0] : undefined;
      if (!lastPt) {
        coordinates.push([head[0], -Math.abs(head[1])]);
      }
      coordinates.push([tail[0], -Math.abs(tail[1])]);
    }
    const geometry = new LineString(coordinates);
    const style = basiceLengthStyle(lengthColors[entityType], 2);
    const lineFeature = new Feature({ geometry });
    lineFeature.setId(id);
    lineFeature.setStyle(style);
    return lineFeature;
  }

  /**
   * == First loop to setup equipment style! ==
   * == will be overrided by `resetFeatureStyle` function ===
   * Build Equipment symbol only, which draw a box and with a text inside of it.
   *
   * @date 2023/05/17
   * @date 2026/05/29 refacter version to fix touch inconsitent
   *
   * @param x
   * @param y
   * @param id
   * @param text
   * @param multiplier
   * @returns
   */
  buildRichLabelFeature(
    x: number,
    y: number,
    id: string,
    text: string,
    multiplier?: string,
    fontSize = DEF,
  ): Feature {
    const nagtiveY = -Math.abs(y);
    // Refactor this method with polygon geometry - @2026/05/29
    // Build a Polygon whose extent matches the rendered label box.
    // OL's hit detection fills the polygon in an offscreen canvas and does a
    // pixel colour lookup — a Point with only a custom renderer produces no
    // pixels there and is never detected. The polygon's first vertex (top-left)
    // is used by the renderer as its drawing anchor. @2026/05/29
    const spaceTotal = 12;
    _measureCtx.font = composeFont(fontSize);
    const textWidth = _measureCtx.measureText(text).width;
    const boxW = textWidth + spaceTotal;
    const boxH = fontSize + spaceTotal;
    // top-left corner: slightly left/above the anchor; Y is negated in map space
    const left = x - spaceTotal / 2;
    const top = nagtiveY + spaceTotal / 2;
    const geometry = new Polygon([
      [
        [left, top], // top-left
        [left + boxW, top], // top-right
        [left + boxW, top - boxH], // bottom-right
        [left, top - boxH], // bottom-left
        [left, top],
      ],
    ]);

    const richLabelFeature = new Feature({ geometry });
    richLabelFeature.setId(id);

    // ! new equipment style support `multiplier`
    // - @2025/10/17
    const style = buildComplexTextStyle(
      text,
      multiplier,
      fontSize,
      'coral',
      false,
    );
    richLabelFeature.setStyle(style);
    return richLabelFeature;
  }

  buildLabelFeature(x: number, y: number, id: string, text: string) {
    const nagtiveY = -Math.abs(y);
    const geometry = new Point([x, nagtiveY]);
    const labelFeature = new Feature({ geometry });
    labelFeature.setId(id);
    const style = new Style({
      text: basicTextStyle(text, 10, false, 'point', false, false, 'red'),
    });
    labelFeature.setStyle(style);
    return labelFeature;
  }

  /**
   * @deprecated no longer in use
   * @param x
   * @param y
   * @param id
   * @returns
   */
  buildECFeature(x: number, y: number, id: string) {
    const ecTxt = EC;
    const nagtiveY = -Math.abs(y);
    const geometry = new Point([x, nagtiveY]);
    const labelFeature = new Feature({ geometry });
    labelFeature.setId(id);
    const style = new Style({
      text: basicTextStyle(ecTxt, 10, false, 'point', false, false, 'red'),
    });
    labelFeature.setStyle(style);
    return labelFeature;
  }

  /**
   * Create a note feature
   * @param x0
   * @param y0 should be positive value
   * @param w
   * @param h
   * @param id
   * @param backgroundColor
   * @param backgroundOpacity
   * @param borderColor
   * @param borderWidth
   * @returns
   */
  buildNoteFeature(
    x0: number,
    y0: number,
    w: number,
    h: number,
    id: string,
    backgroundColor: string,
    fontSize?: number,
  ): Feature {
    if (y0 < 0)
      throw Error('Positive value expected for rect feature creation!');
    const geometry = new Polygon([
      [
        [x0, -y0],
        [x0 + w, -y0],
        [x0 + w, -(y0 + h)],
        [x0, -(y0 + h)],
        [x0, -y0],
      ],
    ]);
    const rectangle = new Feature({ geometry });
    rectangle.setId(id);
    const view = baseNoteRenderStyle(
      backgroundColor,
      DEFAULT_NOTE_TXT,
      fontSize || 12,
    );
    rectangle.setStyle(view);
    return rectangle;
  }

  buildRectFeatureWith(rect: RectSymbol) {
    const { id, x, y, width, height, useDiagonal } = rect;
    return this.buildRectangleFeature(x, y, width, height, id);
  }

  /**
   * Build polygon feature use default style, but:
   * the style here would be overrided in `updateFeatureStyleBy(BPMapCore)`
   *
   * @param x0
   * @param y0 expect to be positive value!
   * @param w
   * @param h
   * @param id
   * @returns
   */
  buildRectangleFeature(
    x0: number,
    y0: number,
    w: number,
    h: number,
    id: string,
  ): Feature {
    if (y0 < 0)
      throw Error('Positive value expected for rect feature creation!');
    const geometry = new Polygon([
      [
        [x0, -y0],
        [x0 + w, -y0],
        [x0 + w, -(y0 + h)],
        [x0, -(y0 + h)],
        [x0, -y0],
      ],
    ]);
    const rectangle = new Feature({ geometry });
    rectangle.setId(id);
    // add style in order to toggle diabled state
    // @2022/12/06
    const strokeFill = basicPolygonStyle();
    rectangle.setStyle(strokeFill);
    return rectangle;
  }

  buildPolygonFeature(coordinates: Coordinate[], id: string) {
    if (!coordinates.length)
      throw Error('No coordinates for polygon feature creation!');
    // ! force the `y` value as negative value for openlayers map:
    const negYCoords: Coordinate[] = coordinates.map((coord) => [
      coord[0],
      // ! make sure its `negative`! - @2026/05/29
      -Math.abs(coord[1]),
    ]);
    const geometry = new Polygon([negYCoords]);
    const polygon = new Feature({ geometry });
    polygon.setId(id);
    const style = basicPolygonStyle();
    polygon.setStyle(style);
    return polygon;
  }

  /**
   * Create text feature to represent an `offset` symbol
   *
   * @param x horizontal position
   * @param y vertical position
   * @param id feature id
   * @param char `X`
   *
   * @returns point feature with text style
   */
  buildSoloCharFeature(
    x: number,
    y: number,
    id: string,
    char: string,
  ): Feature {
    const nagtiveY = -Math.abs(y);
    const geometry = new Point([x, nagtiveY]);
    const richLabelFeature = new Feature({ geometry });
    richLabelFeature.setId(id);
    const text = basicTextStyle(
      char,
      16,
      false,
      'point',
      false, // no need to use rich format
      false,
      DEFAULT_PURPLE_COLOR,
    );
    const style = new Style({ text });
    richLabelFeature.setStyle(style);
    return richLabelFeature;
  }

  /**
   * Delete a feature from drawing and cache
   *
   * @param id
   * @returns
   */
  deleteFeatureById(id: string) {
    const source = this._vectorLayer.getSource();
    if (!source) return;
    const targetFeature = this._features.get(id);
    // const targetFeature = source.getFeatureById(id);
    targetFeature && source.removeFeature(targetFeature);

    // ! may exist in polygon layer - 2026/01/30
    const plgySrc = this._polygonLayer.getSource();
    if (plgySrc && targetFeature) {
      plgySrc.removeFeature(targetFeature);
    }
    // clear cache as well
    this.clearFeature(id);
  }

  deleteFeature(feature: Feature) {
    const source = this._vectorLayer.getSource();
    if (source) {
      source.removeFeature(feature);
    }
    // ! could be polygons on _polygonLayer
    const plgSrc = this._polygonLayer.getSource();
    if (plgSrc) {
      plgSrc.removeFeature(feature);
    }
    const featureId = feature.getId() as string;
    this.clearFeature(featureId);
  }

  deleteLatest() {
    if (!this._latestFeature) return;
    this.deleteFeatureById(this._latestFeature);
  }

  /**
   * Delete a `sketch` feature from drawing and no need process cache
   * @param sketchFeature feature from `draw` tool
   * @returns
   */
  deleteSketchFeature(sketchFeature: Feature) {
    const source = this._vectorLayer.getSource();
    if (!source) return;
    source.removeFeature(sketchFeature);
  }

  /**
   * check which feature is being included by circle
   * @param circle the circle geometry
   * @returns features covered by circle
   */
  checkInclusionBy(circle: Geometry) {
    return this.features.filter((feature) => {
      const geometry = feature.getGeometry();
      if (!geometry) return false;
      const geometryCenter = getCenter(geometry.getExtent());
      // check if the cirle have intersection with the center of each geometry
      return circle.intersectsCoordinate(geometryCenter);
    });
  }

  /**
   * Find out the features in the map that intersects with the given freehand linestring from brush
   * @date 2025/06/04
   * @param lineGeometry line string being painted to check
   * @returns array of features that intersect with the line
   */
  checkCollisionBy(lineGeometry: LineString) {
    const lineExtent = lineGeometry.getExtent();

    return this.features.filter((feature) => {
      const geometry = feature.getGeometry();
      if (!geometry) return false;

      // Fast bounding box check first
      if (!geometry.intersectsExtent(lineExtent)) {
        return false;
      }

      // Check if the line intersects the feature's extent
      // ! this is a banlanced way to check two linestring intersects:
      // ! @2026/02/03
      const featureExtent = geometry.getExtent();
      return lineGeometry.intersectsExtent(featureExtent);
    });
  }

  /**
   * clear feature from cache
   * @param id
   */
  clearFeature(id: string) {
    this._features.delete(id);
  }

  /**
   * clear all the features from map and cache
   */
  clearFeatures() {
    this._vectorLayer.getSource()?.clear(true);
    this._polygonLayer.getSource()?.clear(true);
    this._features.clear();
  }

  /**
   * clear features cache in memory
   */
  cleanup() {
    this._features.clear();
  }

  /**
   * mark the feature `touched` !!
   *
   * - do unified feature patch
   * - do color change - 2025/11/25
   *
   * Update/Merge `detail` of feature,
   * and update the version attached it to enable rerendering!
   *
   * @param featureId
   * @param detail changed dimension info or a whole `FittingEntity`
   * @param brushTheme aka brush color used in rerender feature style
   *
   * @returns
   */
  safePatchFeatureDetail(featureId: string, detail: GO, brushTheme?: string) {
    if (!featureId) return;
    // ! using map key to search - @2026/07/15
    const currentFeature = this._features.get(featureId);
    if (!currentFeature) return warn(`### feature not found: ${featureId}`);
    const origDetail = currentFeature.get(FITTINGDTL);
    const mergedDetail = { ...origDetail, ...detail };

    // derive version from spec rules; confirmed always wins
    const touchedTheme = detail[UVF] ? REV.D : resolveSpecVersion(mergedDetail);

    this.unifiedPatchFeature(
      currentFeature,
      mergedDetail,
      brushTheme || touchedTheme,
    );

    currentFeature.set(FITTINGVER, touchedTheme);

    return currentFeature;
  }

  /**
   * Add necessary info for features: detail, version, label, type
   *
   * the `ONLY` place to set version property to feature with `FITTINGVER`
   *
   * == cache 4 types of properties: ==
   * - detail with FITTINGDTL
   * - version with FITTINGVER
   * - label with FITTINGSIZE
   * - type with FITTINGTYPE
   *
   * FIXME: do not need to check index = 0 for user created symbol use version: 4
   * @date 2024/03/19
   *
   * @param feature
   * @param detail
   * @param vers
   * @param type symbol type to help diffrentiate the style - 2023/05/18
   * @param text equipment text
   */
  unifiedPatchFeature(
    feature: Feature,
    detail: GO,
    vers?: string,
    type?: SymbolType,
    text?: string,
  ) {
    // == cache: 1. ukey
    feature.set('ukey', detail['ukey'] || 'unset');
    // == cache: 2.  detail
    feature.set(FITTINGDTL, detail);

    // == cache: 3. version
    const lastVersion = feature.get(FITTINGVER);
    if (!lastVersion) {
      // first time version marking!
      feature.set(FITTINGVER, vers || REV.B);
    }
    const numVersionNew = Number(vers);
    // ! version may changed above! - @2025/05/22
    const numVersionLast = Number(feature.get(FITTINGVER));
    const isEscalated =
      numVersionNew > 0 && numVersionLast > 0 && numVersionNew > numVersionLast;
    // ! FIXME: allowing escalate version but not degrade version:
    // @2025/02/07
    if (isEscalated) {
      // higher version marking!
      feature.set(FITTINGVER, vers);
    }

    // == cache: 4. label ==
    // second place to use `composeLabel`, the first is in transformer
    const label = composeLabelBySymbol(detail);
    // console.log(`>> label after patch: ${label}`);
    feature.set(FITTINGSIZE, label);
    // override the text from TextSymbol- @2023/05/18
    text && feature.set(FITTINGSIZE, text);
    // == cache: 5. type ==
    // attach symbol type to feature - @2023/05/18
    type && feature.set(FITTINGTYPE, type);

    // == cache: 6. multiplier ==
    // ! check `multiplier` to feature to render later
    // @2025/12/09
    const multiplier = detail[FIELD_MP];
    multiplier && feature.set(FIELD_MP, multiplier);

    // == cache: 7. EC/STO/diagonal flags derived from detail ==
    const setOrClearFlag = (key: string, on: boolean) =>
      on ? feature.set(key, true) : feature.unset(key, true);

    setOrClearFlag(FITTING_EC, !!detail['hasEC']);
    setOrClearFlag(LENGTH_DG, detail['use_diagonal'] === 'true');
    setOrClearFlag(FITTING_STO, detail['add_take_off'] === 'true');
  }
}
